import { useEffect, useRef, useState } from 'react'
import { useInView, useReducedMotion } from 'motion/react'
import { ArrowRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { ScrollReveal, TextReveal, useMediaQuery } from '@/components/motion'
import { OfficeAppIcon } from '@/components/landing/OfficeAppIcon'
import { HALFTONE_STYLE, Highlight } from '@/components/landing/about/Highlight'
import type { SolutionData, SolutionStep } from '@/data/solutions'

const TILE_BG = ['#ff00f2', 'var(--color-landing-accent)', 'var(--color-landing-secondary)', '#0a0a0a'] as const
const EASE = 'ease-[cubic-bezier(0.22,1,0.36,1)]'
const pad = (n: number) => String(n + 1).padStart(2, '0')

/** Tile art: the step's apps and sources as white chips, like a task pane list. */
function NodeChips({ step }: { step: SolutionStep }) {
  return (
    <ul className="absolute right-[6%] top-[9%] grid w-[52%] gap-[0.4rem] sm:right-[7%] sm:top-[11%] sm:w-[44%] sm:gap-[0.6rem]">
      {step.nodes.map((node, i) => (
        <li
          key={`${node.label}-${i}`}
          className="flex min-w-0 items-center gap-2 bg-white px-2 py-1.5 shadow-[0_14px_24px_-16px_rgb(0_0_0_/_0.5)] sm:gap-3 sm:px-3 sm:py-2.5"
        >
          {node.app ? (
            <OfficeAppIcon app={node.app} className="h-5 w-5 shrink-0 sm:h-7 sm:w-7" />
          ) : (
            <span className="block h-5 w-5 shrink-0 bg-[var(--color-landing-primary)] sm:h-7 sm:w-7" />
          )}
          <span className="min-w-0 truncate text-[11px] font-semibold text-[var(--color-landing-gray-800)] sm:text-sm">
            {node.label}
          </span>
          <span className="ml-auto hidden h-1.5 w-8 shrink-0 bg-[var(--color-landing-gray-200)] sm:block" />
        </li>
      ))}
    </ul>
  )
}

function StepTile({
  step,
  index,
  bright,
  tileRef,
}: {
  step: SolutionStep
  index: number
  bright: boolean
  tileRef: (el: HTMLDivElement | null) => void
}) {
  return (
    <li>
      <div
        ref={tileRef}
        aria-hidden
        className={cn(
          'relative aspect-[3/2] overflow-hidden [container-type:inline-size] transition-opacity duration-700 dark:ring-1 dark:ring-inset dark:ring-white/10',
          EASE
        )}
        style={{ background: TILE_BG[index % TILE_BG.length], opacity: bright ? 1 : 0.5 }}
      >
        <div className="absolute inset-0 opacity-50" style={HALFTONE_STYLE} />
        <NodeChips step={step} />
        <span className="absolute bottom-[5%] left-[5%] font-[family-name:var(--font-landing-display)] text-[clamp(2.5rem,11cqw,7rem)] uppercase leading-[1.1] tracking-[-0.03em] text-white">
          {pad(index)}
        </span>
      </div>
      {/* Accessible copy; visible under each tile below lg, where the crossfading slot is hidden. */}
      <div className="mt-5 lg:sr-only">
        <h3 className="font-[family-name:var(--font-landing-subdisplay)] text-xl font-bold uppercase tracking-[-0.01em] text-[var(--color-landing-gray-900)] dark:text-white">
          {step.title}
        </h3>
        <p className="landing-text-body mt-3 text-[var(--color-landing-gray-700)] dark:text-[var(--color-landing-gray-300)]">
          {step.description}
        </p>
      </div>
    </li>
  )
}

/**
 * The about page's journey timeline, adapted for a solution's steps
 * (.4fr / 1fr): sticky heading column whose step slot crossfades to the tile
 * in the viewport's centre band; 3:2 tiles in brand colours with the step's
 * apps as chips; a 5px centre line (mix-blend-mode: difference) with a pink
 * fill whose head sticks to the viewport centre. Static flow below lg.
 */
export function StepJourney({ solution }: { solution: SolutionData }) {
  const desktop = useMediaQuery('(min-width: 1024px)')
  const reduce = useReducedMotion()
  const listRef = useRef<HTMLOListElement>(null)
  const tileRefs = useRef<(HTMLDivElement | null)[]>([])
  const near = useInView(listRef, { margin: '50% 0px 50% 0px' })
  const [active, setActive] = useState(0)
  const [inBand, setInBand] = useState(-1)
  const steps = solution.steps

  useEffect(() => {
    if (!desktop || !near) return
    let frame = 0
    const measure = () => {
      frame = 0
      const mid = window.innerHeight / 2
      const half = window.innerHeight * 0.05
      let nextActive = 0
      let nextBand = -1
      tileRefs.current.forEach((el, i) => {
        if (!el) return
        const r = el.getBoundingClientRect()
        if (r.top <= mid) nextActive = i
        if (r.top < mid + half && r.bottom > mid - half) nextBand = i
      })
      setActive(nextActive)
      setInBand(nextBand)
    }
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(measure)
    }
    measure()
    window.addEventListener('scroll', schedule, { passive: true })
    window.addEventListener('resize', schedule)
    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener('scroll', schedule)
      window.removeEventListener('resize', schedule)
    }
  }, [desktop, near])

  const dimmable = desktop && !reduce
  const titleId = `${solution.slug}-steps-title`

  return (
    <section id="how-it-works" aria-labelledby={titleId} className="landing-section scroll-mt-24">
      <div className="landing-container grid items-start gap-14 lg:grid-cols-[minmax(0,0.4fr)_minmax(0,1fr)] lg:gap-20">
        <div className="lg:sticky lg:top-28 lg:flex lg:h-[calc(100svh-9rem)] lg:flex-col">
          <TextReveal as="h2" className="landing-text-display-xl text-[var(--color-landing-gray-900)] dark:text-white">
            <span id={titleId}>
              How Templatean works for <Highlight color="pink">{solution.teamName.toLowerCase()} teams.</Highlight>
            </span>
          </TextReveal>
          <ScrollReveal direction="none" delay={0.15}>
            <Link
              to="/contact"
              className="group mt-10 inline-flex items-center gap-3 font-[family-name:var(--font-landing-display)] text-lg uppercase text-[var(--color-landing-gray-900)] transition-colors duration-200 hover:text-[var(--color-landing-accent)] dark:text-white"
            >
              Book a demo
              <ArrowRight aria-hidden className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-0.5" />
            </Link>
          </ScrollReveal>

          {/* Crossfading step slot (visual, desktop only; the tiles carry the copy) */}
          {desktop && (
          <div aria-hidden className="mt-auto hidden pt-10 lg:grid">
            {steps.map((step, i) => (
              <div
                key={step.title}
                className={cn('[grid-area:1/1] transition-[opacity,transform] duration-500', EASE)}
                style={{
                  opacity: i === active ? 1 : 0,
                  transform: i === active ? 'translateY(0)' : 'translateY(10px)',
                }}
              >
                <p className="font-[family-name:var(--font-landing-display)] text-[1.6rem] uppercase leading-[1.4] tracking-[-0.02em] text-[var(--color-landing-gray-900)] dark:text-white">
                  {step.title}
                </p>
                <span className="mt-4 block h-px w-16 bg-[var(--color-landing-gray-300)] dark:bg-[var(--color-landing-gray-700)]" />
                <p className="landing-text-body mt-4 max-w-[42ch] text-[color-mix(in_srgb,var(--color-landing-gray-900)_66%,transparent)] dark:text-[color-mix(in_srgb,white_62%,transparent)]">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
          )}
        </div>

        <div className="relative">
          {/* Centre line, inverted against whatever it crosses */}
          <div aria-hidden className="pointer-events-none absolute inset-y-0 left-1/2 z-10 hidden w-[5px] -translate-x-1/2 bg-white mix-blend-difference lg:block" />
          {/* Pink fill whose head sticks at the viewport centre */}
          <div aria-hidden className="pointer-events-none absolute inset-y-0 left-1/2 z-20 hidden w-4 -translate-x-1/2 [overflow:clip] lg:block">
            <div className="sticky top-[50vh] h-0">
              <div className="absolute bottom-0 left-1/2 h-[200vh] w-[5px] -translate-x-1/2 bg-[#ff00f2]" />
              <div className="absolute left-1/2 top-0 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 bg-[#ff00f2]" />
            </div>
          </div>

          <ol ref={listRef} className="grid gap-16 lg:gap-24">
            {steps.map((step, index) => (
              <StepTile
                key={step.title}
                step={step}
                index={index}
                bright={!dimmable || index === inBand}
                tileRef={(el) => {
                  tileRefs.current[index] = el
                }}
              />
            ))}
          </ol>
        </div>
      </div>
    </section>
  )
}

export default StepJourney
